import * as React from "react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { vw } from "../../../lib/utils/responsiveUtils";
dayjs.locale("ko");

type Props = {
  value?: string; // 선택된 날짜 "YYYY-MM-DD"
  onChange?: (v: string) => void; // 클릭 시 선택 변경 콜백
  days?: number; // 노출 일수 (기본 14일)
  startDate?: string; // 시작일(기본 오늘)
  endDate?: string; // 종료일(선택 시 days 무시)
  untilMonth?: string; // "YYYY-MM" 형태 (예: "2025-12")
};

const WEEK = ["일", "월", "화", "수", "목", "금", "토"];

type DateItem = {
  key: string;
  monthLabel: string;
  week: string;
  day: string;
  dayIndex: number;
};

export default function DateStrip({
  value,
  onChange,
  days = 14,
  startDate,
  endDate,
  untilMonth,
}: Props) {
  const { startDay, endDay } = React.useMemo(() => {
    // 1) startDate + endDate가 둘 다 있으면 이 구간을 최우선으로 사용
    if (startDate && endDate) {
      const s = dayjs(startDate).startOf("day");
      const e = dayjs(endDate).startOf("day");
      return { startDay: s, endDay: e };
    }

    // 기본 시작일
    const s = (startDate ? dayjs(startDate) : dayjs()).startOf("day");

    // 2) 그다음이 untilMonth (종료 '월' 기준)
    if (untilMonth) {
      const e = dayjs(untilMonth).endOf("month").startOf("day");
      return { startDay: s, endDay: e };
    }

    // 3) startDate 없이 endDate만 있을 때
    if (endDate) {
      const e = dayjs(endDate).startOf("day");
      return { startDay: s, endDay: e };
    }

    // 4) 다 없으면 days 기준
    const e = s.add(Math.max(0, days - 1), "day");
    return { startDay: s, endDay: e };
  }, [startDate, endDate, untilMonth, days]);

  // 날짜 배열 생성
  const items = React.useMemo<DateItem[]>(() => {
    const total = endDay.diff(startDay, "day") + 1;
    return Array.from({ length: total }, (_, i) => {
      const d = startDay.add(i, "day");
      return {
        key: d.format("YYYYMMDD"),
        monthLabel: d.format("M월"),
        week: WEEK[d.day()],
        day: d.format("D"),
        dayIndex: d.day(),
      };
    });
  }, [startDay, endDay]);

  const selected = value ?? items[0]?.key;

  // 월별 그룹으로 묶기
  const monthGroups = React.useMemo(() => {
    const groups: { month: string; days: DateItem[] }[] = [];

    items.forEach((it) => {
      const last = groups[groups.length - 1];
      if (!last || last.month !== it.monthLabel) {
        groups.push({ month: it.monthLabel, days: [it] });
      } else {
        last.days.push(it);
      }
    });

    return groups;
  }, [items]);

  return (
    <div className="space-y-2">
      <div
        className="overflow-x-auto no-scrollbar"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <style>{`div::-webkit-scrollbar { display: none; }`}</style>

        {/* 월 그룹들을 가로로 나열 */}
        <div className="inline-flex">
          {monthGroups.map((group, groupIndex) => (
            <div
              key={`${group.month}-${groupIndex}`}
              className={`flex flex-col ${groupIndex === 0 ? "pl-[15px]" : "ml-[10px]"}`}
            >
              {/* 월 + 점선 */}
              <div className="flex items-center">
                <div className="text-[#222] font-bold whitespace-nowrap">
                  {group.month}
                </div>
                <div
                  className="flex-1 ml-[10px]"
                  style={{
                    height: "4px",
                    backgroundImage:
                      "radial-gradient(circle, #e6e6e6 1px, transparent 2px)",
                    backgroundSize: "6px 3px",
                    backgroundRepeat: "repeat-x",
                  }}
                />
              </div>

              {/* 날짜 버튼 리스트 */}
              <div className="mt-[15px] flex pb-[2px] gap-[10px]">
                {group.days.map((it) => {
                  const isSelected = it.key === selected;

                  let textColor = "#222";
                  if (it.dayIndex === 0) textColor = "#e5002b";
                  else if (it.dayIndex === 6) textColor = "#155be9";

                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => onChange?.(it.key)}
                      style={{ width: vw(71), height: vw(74) }}
                      className={[
                        "shrink-0 rounded-[10px] bg-white",
                        "flex flex-col items-center justify-center",
                        "border transition-colors",
                        isSelected ? "border-[#222]" : "border-[#e6e6e6]",
                      ].join(" ")}
                    >
                      <div
                        className="font-[500] text-sm"
                        style={{ color: textColor }}
                      >
                        {it.week}
                      </div>
                      <div
                        className="font-bold"
                        style={{ fontSize: vw(20), color: textColor }}
                      >
                        {it.day}일
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
